/**
 * Sentry error tracking for the web app (consensus plan §2.10 / Architect §5).
 *
 * Initialized BEFORE React mounts in main.tsx so SDK init failures or first-
 * paint errors are captured. The SDK is lazy-loaded via dynamic import to
 * keep the launcher chunk under 70 KB gz — Sentry browser core is ~30 KB gz
 * and lands in its own vendor chunk after the initial load.
 *
 * VITE_SENTRY_DSN env var controls activation. Empty / missing → no-op
 * (dev mode and self-hosted previews without telemetry).
 */

let initialized = false

export async function initSentryWeb(): Promise<void> {
  if (initialized) return
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  try {
    const Sentry = await import('@sentry/browser')
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.1,
      // Don't auto-capture every console.error — most TMA SDK warnings are noise.
      defaultIntegrations: false,
      integrations: [
        Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
        Sentry.functionToStringIntegration(),
      ],
    })
    initialized = true
    // Tag with Telegram user_id if available (sanitized — no first_name).
    const tg = window.Telegram?.WebApp
    const uid = tg?.initDataUnsafe?.user?.id
    if (uid) {
      Sentry.setUser({ id: String(uid) })
    }
  } catch (err) {
    // Never let Sentry init failures block app boot.
    // eslint-disable-next-line no-console
    console.warn('[sentry] init failed:', err)
  }
}

/**
 * Manually capture an exception. Returns immediately even if Sentry isn't
 * initialized — never blocks the calling code path.
 */
export function captureSentryException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return
  void import('@sentry/browser').then((Sentry) => {
    if (context) {
      Sentry.withScope((scope) => {
        scope.setContext('sagwa-box', context)
        Sentry.captureException(err)
      })
    } else {
      Sentry.captureException(err)
    }
  })
}
